import RiveCanvas, {
  RiveCanvas as RiveCanvasType,
} from '@rive-app/canvas-advanced';

/**
 * Singleton class responsible for loading and managing the Rive runtime.
 *
 * This class provides a centralized way to:
 * - Load the Rive WebAssembly (WASM) runtime once
 * - Share a single runtime instance across the application
 * - Queue callbacks waiting for the runtime to load
 * - Handle loading errors gracefully
 *
 * The class uses a singleton pattern to ensure only one runtime is loaded,
 * improving performance and resource usage.
 */
export default class RuntimeLoader {
  /** The single instance of the Rive runtime */
  private static runtime: RiveCanvasType | null = null;

  /** Flag indicating whether the runtime is currently being loaded */
  private static isLoading = false;

  /** Queue of callbacks waiting for the runtime to be loaded */
  private static callBackQueue: ((rive: RiveCanvasType | null) => void)[] = [];

  /** URL to the Rive WASM file */
  private static wasmURL =
    'https://unpkg.com/@rive-app/canvas-advanced@2.26.4/rive.wasm';

  /**
   * Private constructor to prevent instantiation of this utility class.
   * All methods are static and should be accessed directly from the class.
   */
  private constructor() {}

  /**
   * Loads the Rive runtime asynchronously.
   *
   * This method:
   * 1. Attempts to load the Rive WASM file from the configured URL
   * 2. Stores the runtime instance if successful
   * 3. Processes all queued callbacks with the runtime or null (on error)
   * 4. Resets the loading state and queue
   *
   * @returns {Promise<void>} A promise that resolves when loading completes
   * @private
   */
  private static async loadRuntime(): Promise<void> {
    try {
      // Initialize the Rive runtime with the configured WASM URL
      const rive = await RiveCanvas({
        locateFile: () => RuntimeLoader.wasmURL,
      });

      // Store the runtime instance for future use
      RuntimeLoader.runtime = rive;

      // Process all queued callbacks with the successful runtime
      RuntimeLoader.callBackQueue.forEach((cb) => cb(rive));
    } catch (error) {
      // Log the error and notify all callbacks about the failure
      console.error('Error loading Rive runtime:', error);
      RuntimeLoader.callBackQueue.forEach((cb) => cb(null));
    } finally {
      // Reset the loading state and clear the queue
      RuntimeLoader.isLoading = false;
      RuntimeLoader.callBackQueue = [];
    }
  }

  /**
   * Provides access to the Rive runtime instance via a callback.
   *
   * If the runtime is already loaded, the callback is executed immediately.
   * Otherwise, the callback is queued and the runtime loading is initiated
   * if it's not already in progress.
   *
   * @param {Function} callback - Function to call with the runtime instance
   *                             or null if loading fails
   */
  public static getInstance(callback: (rive: RiveCanvasType | null) => void) {
    if (RuntimeLoader.runtime) {
      // If runtime is already loaded, execute callback immediately
      callback(RuntimeLoader.runtime);
    } else {
      // Queue the callback for when loading completes
      RuntimeLoader.callBackQueue.push(callback);

      // Start loading if not already in progress
      if (!RuntimeLoader.isLoading) {
        RuntimeLoader.isLoading = true;
        RuntimeLoader.loadRuntime();
      }
    }
  }

  /**
   * Provides access to the Rive runtime instance via a Promise.
   *
   * This method is an async-friendly alternative to getInstance that
   * returns a Promise instead of using a callback.
   *
   * @returns {Promise<RiveCanvasType | null>} Promise resolving to the runtime
   *                                          instance or null if loading fails
   *
   */
  public static async awaitInstance(): Promise<RiveCanvasType | null> {
    return new Promise((resolve) => RuntimeLoader.getInstance(resolve));
  }

  /**
   * Updates the URL used to load the Rive WASM file.
   *
   * This method allows changing the source of the WASM file dynamically.
   * If the URL is changed, the runtime will be reset and loaded from the
   * new URL on the next getInstance or awaitInstance call.
   *
   * @param {string} url - The new URL to the Rive WASM file
   */
  public static setWasmUrl(url: string) {
    if (url && url !== RuntimeLoader.wasmURL) {
      // Update the WASM URL
      RuntimeLoader.wasmURL = url;

      // Reset the runtime to force reload on next getInstance call
      RuntimeLoader.runtime = null;
    }
  }
}
